import { computed, type Signal } from '@angular/core';
import {
  type ObjectResultsTableColumnId,
  type ObjectResultsTableColumnWidths,
  type SearchResultItem,
} from '@bizzdesign/data-access-search';
import type { Table } from 'primeng/table';
import {
  isObjectResultsColumnId,
  type ObjectResultsColumn,
} from '../../utils/object-results-columns.utils';

/** Width in pixels of the leading checkbox column. */
const SELECTION_COLUMN_WIDTH_PX = 48;

/** Width in pixels of the trailing row-actions column. */
const ACTIONS_COLUMN_WIDTH_PX = 64;

/** Narrowest a column may be dragged to. */
const MINIMUM_COLUMN_WIDTH_PX = 48;

/**
 * What the sizing helper needs from the table component.
 *
 * @property columns - Columns currently rendered, in order.
 * @property showSelectionColumn - Whether the leading checkbox column is shown.
 * @property table - The PrimeNG table, once its view child resolves.
 * @property resizedColumnWidths - Widths the user dragged columns to, which the
 * store owns so that resetting the layout clears them along with the columns.
 * @property setColumnWidth - Records the width a resize gesture produced.
 */
export type ObjectResultsColumnSizingDeps = {
  readonly columns: Signal<ObjectResultsColumn[]>;
  readonly showSelectionColumn: Signal<boolean>;
  readonly table: Signal<Table<SearchResultItem> | undefined>;
  readonly resizedColumnWidths: Signal<ObjectResultsTableColumnWidths>;
  readonly setColumnWidth: (
    columnId: ObjectResultsTableColumnId,
    width: number,
  ) => void;
};

/**
 * Turns the column widths into CSS lengths, places the pinned columns, and
 * clears the state PrimeNG's own resize gesture leaves behind.
 *
 * @remarks
 * Every column carries its own width in pixels, so the table is exactly as wide
 * as its columns and the container scrolls when that exceeds it. Showing or
 * hiding a column therefore never changes the width of any other, and the
 * pinned columns keep the offsets they had.
 *
 * The widths a resize gesture produces are read from and written to the store
 * through `deps`, not held here, so that resetting the layout clears them along
 * with the columns however the reset was triggered.
 *
 * Split out of the table component because it is a self-contained concern with
 * its own DOM reach into PrimeNG, and because it needs no injection context: a
 * test can drive it with plain signals.
 */
export class ObjectResultsColumnSizing {
  /** Width of the leading checkbox column, as a CSS length. */
  readonly selectionColumnWidth = `${SELECTION_COLUMN_WIDTH_PX}px`;

  /** Width of the trailing row-actions column, as a CSS length. */
  readonly actionsColumnWidth = `${ACTIONS_COLUMN_WIDTH_PX}px`;

  /** Each rendered column with the width it should get, in render order. */
  private readonly renderedColumnWidths = computed(() => {
    const resizedColumnWidths = this.deps.resizedColumnWidths();
    return this.deps.columns().map(({ id, pinned, width }) => ({
      id,
      pinned,
      width: resizedColumnWidths[id] ?? width,
    }));
  });

  /**
   * The CSS width of each rendered column.
   *
   * @remarks
   * Derived from `renderedColumnWidths` rather than resolved again, so the
   * widths on the `col` elements and the table's own width can never be built
   * from different numbers — `table-layout: fixed` would stretch the columns to
   * absorb the difference.
   */
  private readonly columnWidthsById = computed(
    () =>
      new Map(
        this.renderedColumnWidths().map(
          ({ id, width }) => [id, `${width}px`] as const,
        ),
      ),
  );

  /**
   * The `style` the table element itself carries.
   *
   * @remarks
   * The width is the sum of every rendered column, the checkbox and actions
   * columns included, so the table is exactly as wide as its columns and the
   * container scrolls instead of a column stretching.
   */
  readonly tableStyle = computed(() => ({
    // Without `fixed`, the browser treats the `<col>` widths as suggestions and
    // stretches columns to fill the table, which is the behaviour this model
    // replaces.
    tableLayout: 'fixed',
    width: `${
      (this.deps.showSelectionColumn() ? SELECTION_COLUMN_WIDTH_PX : 0) +
      this.renderedColumnWidths().reduce(
        (totalWidth, { width }) => totalWidth + width,
        0,
      ) +
      ACTIONS_COLUMN_WIDTH_PX
    }px`,
  }));

  /**
   * The `left` offset of each pinned column.
   *
   * @remarks
   * Summed from the widths rather than measured from the rendered header cells,
   * so it is right on the first render and needs no frame to settle.
   */
  private readonly pinnedColumnOffsetsById = computed(() => {
    const offsets = new Map<ObjectResultsTableColumnId, string>();
    let currentOffset = this.deps.showSelectionColumn()
      ? SELECTION_COLUMN_WIDTH_PX
      : 0;
    for (const column of this.renderedColumnWidths()) {
      if (!column.pinned) {
        continue;
      }
      offsets.set(column.id, `${currentOffset}px`);
      currentOffset += column.width;
    }
    return offsets;
  });

  constructor(private readonly deps: ObjectResultsColumnSizingDeps) {}

  /**
   * The CSS width for one column.
   *
   * @param column - Column to size.
   * @returns A CSS length.
   */
  columnWidth(column: ObjectResultsColumn): string {
    return this.columnWidthsById().get(column.id) ?? `${column.width}px`;
  }

  /**
   * The `left` offset that keeps a pinned column stuck in place.
   *
   * @param column - Column to position.
   * @returns A CSS length, or `null` for a column that is not pinned.
   */
  stickyLeft(column: ObjectResultsColumn): string | null {
    return this.pinnedColumnOffsetsById().get(column.id) ?? null;
  }

  /**
   * Records the width PrimeNG's resize gesture produced, for that column alone.
   *
   * @param event - Resize event emitted by the PrimeNG table.
   */
  onColumnResize(event: { element: HTMLElement; delta: number }): void {
    const resizedColumnId = this.getColumnIdFromHeaderDataId(
      event.element.dataset['id'],
    );
    const resizedColumn = this.deps
      .columns()
      .find(({ id }) => id === resizedColumnId);
    if (!resizedColumn) {
      return;
    }
    const currentWidth =
      this.deps.resizedColumnWidths()[resizedColumn.id] ?? resizedColumn.width;
    this.deps.setColumnWidth(
      resizedColumn.id,
      Math.max(MINIMUM_COLUMN_WIDTH_PX, Math.round(currentWidth + event.delta)),
    );
    requestAnimationFrame(() => this.clearPrimeTableResizeState());
  }

  /**
   * Undoes the inline width PrimeNG's resize gesture writes, which would
   * otherwise fight the widths this helper puts on the `<col>` elements.
   *
   * @remarks
   * The table width is restored rather than removed. Angular wrote it from
   * `tableStyle`, and it does not write it again while the computed value is
   * unchanged, so clearing it would leave the table filling its parent — and
   * `table-layout: fixed` would then stretch every column to share the excess.
   */
  clearPrimeTableResizeState(): void {
    const table = this.deps.table();
    if (!table) {
      return;
    }
    table.destroyStyleElement();
    table.setResizeTableWidth('');
    const nativeTable = this.getNativeTableElement(table);
    if (!nativeTable) {
      return;
    }
    nativeTable.style.removeProperty('min-width');
    nativeTable.style.setProperty('width', this.tableStyle().width);
  }

  private getColumnIdFromHeaderDataId(
    dataId: string | null | undefined,
  ): ObjectResultsTableColumnId | undefined {
    if (!dataId?.startsWith('search-item-') || !dataId.endsWith('-header')) {
      return undefined;
    }
    const columnId = dataId.slice('search-item-'.length, -'-header'.length);
    return isObjectResultsColumnId(columnId) ? columnId : undefined;
  }

  private getNativeTableElement(
    table: Table<SearchResultItem>,
  ): HTMLElement | undefined {
    const nativeTable = table.tableViewChild?.nativeElement;
    return nativeTable instanceof HTMLElement ? nativeTable : undefined;
  }
}
